import React from "react";
import { cn } from "@repo/core";

interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}

interface AppTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyProp: keyof T;
  emptyMessage?: string;
  className?: string;
  onRowClick?: (row: T) => void;
  stickyHeader?: boolean;
}

export function AppTable<T extends object>({
  columns,
  data,
  keyProp,
  emptyMessage = "",
  className,
  onRowClick,
  stickyHeader,
}: AppTableProps<T>) {
  return (
    <div
      className={cn("app-card overflow-hidden", className)}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={cn("border-b border-border bg-muted/50", stickyHeader && "sticky top-0 z-10")}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center",
                    !col.align && "text-left",
                    col.className
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-muted-foreground text-xs">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={String(row[keyProp])}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "bg-card transition-colors",
                    onRowClick && "cursor-pointer hover:bg-accent/40"
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-3 py-2.5",
                        col.align === "right" && "text-right",
                        col.align === "center" && "text-center",
                        col.className
                      )}
                    >
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
